import { Link } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  collections,
  formatViews,
  getFeaturedMovies,
  getHotRanking,
  getLatestMovies,
} from '@/lib/movies';

const quickLinks = [
  { icon: 'ri-time-line', title: '最新上架', desc: '每日补充新的高清片源', to: '/category/latest' },
  { icon: 'ri-fire-line', title: '人气热榜', desc: '按播放量筛选的高分作品', to: '/category/hot' },
  { icon: 'ri-film-line', title: '经典修复', desc: '重看那些被时间留下的镜头', to: '/category/classic' },
  { icon: 'ri-search-2-line', title: '关键词搜索', desc: '标题、演员、标签一次搜全', to: '/search' },
];

const guideItems = [
  { icon: 'ri-shield-check-line', title: '全部免费浏览', desc: '站内展示的影片资料不设付费墙，注册与登录都不是必要条件。' },
  { icon: 'ri-hard-drive-2-line', title: '收藏保存在本地', desc: '收藏记录只写入当前浏览器，不上传、不共享，换设备不会同步。' },
  { icon: 'ri-information-line', title: '播放为交互演示', desc: '播放器只模拟播放状态与控制条，不加载任何真实视频文件。' },
];

export default function Home() {
  usePageMeta({
    title: 'av免费网站-精选高清AV电影在线观看平台',
    description:
      'av免费网站提供丰富多样的AV影视内容，覆盖最新与经典作品，满足用户免费观看需求，轻松在线浏览和搜索想看的影片。',
    keywords: 'av免费网站,AV在线观看,AV电影,免费视频,高清AV,在线电影,高清影视,免费视频平台',
  });

  const featured = getFeaturedMovies();
  const latest = getLatestMovies(8);
  const ranking = getHotRanking(5);

  return (
    <>
      <HeroCarousel items={featured} />

      <section className="border-y border-background-200/70 bg-background-100">
        <div className="max-w-page mx-auto px-4 md:px-8 py-6 md:py-8 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {quickLinks.map((item) => (
            <Link
              key={item.title}
              to={item.to}
              className="group flex items-start gap-3 p-3.5 md:p-4 rounded-lg border border-background-300/50 bg-background-50/60 hover:border-primary-500/50 transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 md:w-10 md:h-10 shrink-0 rounded-md bg-primary-100 text-primary-300 flex items-center justify-center">
                <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm md:text-[15px] font-medium text-foreground-950 group-hover:text-primary-400 transition-colors">
                  {item.title}
                </span>
                <span className="mt-1 block text-[11px] md:text-xs leading-relaxed text-foreground-500">
                  {item.desc}
                </span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-page mx-auto px-4 md:px-8 pt-14 md:pt-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">
              Curated Collections
            </p>
            <h2 className="mt-2 text-xl md:text-3xl font-bold tracking-tight text-foreground-950">
              编辑精选片单
            </h2>
            <p className="mt-2 text-sm text-foreground-600 max-w-xl">
              按主题重新编排的影片合集，比分类更具体，也比榜单更耐看。
            </p>
          </div>
          <Link
            to="/category/hot"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            全部合集
            <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center" />
          </Link>
        </div>

        <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {collections.map((collection) => (
            <Link
              key={collection.id}
              to={collection.to}
              className="group relative block w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200 cursor-pointer"
            >
              <img
                src={collection.image}
                alt={`${collection.title} 片单封面`}
                title={`${collection.title} - 精选片单`}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/45 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                <span className="inline-flex items-center h-6 px-2 rounded-md bg-background-50/75 backdrop-blur-sm text-[10px] tracking-wider text-secondary-700 font-label">
                  {collection.meta}
                </span>
                <h3 className="mt-2.5 text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-400 transition-colors">
                  {collection.title}
                </h3>
                <p className="mt-1 text-xs text-foreground-600">{collection.subtitle}</p>
              </div>
              <span className="absolute top-3 right-3 w-8 h-8 rounded-full border border-foreground-800/40 text-foreground-800 group-hover:bg-primary-500 group-hover:border-primary-500 group-hover:text-background-50 dark:group-hover:text-foreground-950 flex items-center justify-center transition-colors">
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-page mx-auto px-4 md:px-8 pt-14 md:pt-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">New This Week</p>
            <h2 className="mt-2 text-xl md:text-3xl font-bold tracking-tight text-foreground-950">最新更新</h2>
            <p className="mt-2 text-sm text-foreground-600">按更新时间排序，最近七天上架的影片。</p>
          </div>
          <Link
            to="/category/latest"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-background-300/70 text-sm text-foreground-700 hover:border-primary-500/60 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
          </Link>
        </div>

        <div className="mt-6 md:mt-8 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {latest.map((movie, index) => (
            <div key={movie.id} style={{ animationDelay: `${index * 40}ms` }}>
              <MovieCard movie={movie} />
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-page mx-auto px-4 md:px-8 pt-14 md:pt-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5 md:gap-6">
          <div className="rounded-lg border border-background-300/60 bg-background-100 p-4 md:p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">Weekly Chart</p>
                <h2 className="mt-2 text-xl md:text-2xl font-bold tracking-tight">本周热榜</h2>
              </div>
              <span className="h-8 px-3 rounded-md bg-accent-500/15 border border-accent-500/25 text-accent-300 text-xs flex items-center whitespace-nowrap">
                实时更新
              </span>
            </div>

            <div className="mt-4 divide-y divide-background-200/80">
              {ranking.map((movie, index) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="group flex items-center gap-3 md:gap-4 py-3 md:py-3.5 cursor-pointer"
                >
                  <span className="w-7 md:w-8 shrink-0 font-label text-xl md:text-2xl text-foreground-400 group-hover:text-primary-400 transition-colors">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="w-24 md:w-28 h-14 md:h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img
                      src={movie.cover}
                      alt={`${movie.title} 缩略图`}
                      title={`${movie.title} - 热门影片`}
                      className="w-full h-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm md:text-[15px] font-medium text-foreground-950 line-clamp-1 group-hover:text-primary-400 transition-colors">
                      {movie.title}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-500">
                      <span>{movie.year}</span>
                      <span className="w-1 h-1 rounded-full bg-background-400" />
                      <span>{movie.region}</span>
                      <span className="w-1 h-1 rounded-full bg-background-400" />
                      <span>{movie.tags.join(' / ')}</span>
                    </span>
                  </span>
                  <span className="hidden sm:flex flex-col items-end gap-1 shrink-0">
                    <span className="inline-flex items-center gap-1 text-xs text-primary-400">
                      <i className="ri-star-fill w-3.5 h-3.5 flex items-center justify-center" />
                      {movie.rating.toFixed(1)}
                    </span>
                    <span className="text-[11px] text-foreground-500">{formatViews(movie.views)} 次</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <aside className="rounded-lg border border-background-300/60 bg-secondary-50 p-4 md:p-6">
            <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">Before You Watch</p>
            <h2 className="mt-2 text-xl md:text-2xl font-bold tracking-tight">观影前须知</h2>
            <div className="mt-5 space-y-5">
              {guideItems.map((item) => (
                <div key={item.title} className="flex gap-3">
                  <span className="w-8 h-8 shrink-0 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center">
                    <i className={`${item.icon} text-base w-4 h-4 flex items-center justify-center`} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-950">{item.title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/faq"
              className="mt-6 inline-flex items-center justify-center gap-2 w-full h-11 rounded-md border border-secondary-300/50 text-sm text-secondary-900 hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看常见问题
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </aside>
        </div>
      </section>

      <section className="max-w-page mx-auto px-4 md:px-8 pt-14 md:pt-20">
        <div className="relative w-full rounded-lg overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20editorial%20still%20of%20a%20cozy%20home%20cinema%20corner%2C%20warm%20amber%20lamp%20glow%20on%20a%20deep%20crimson%20wall%2C%20film%20posters%20and%20vinyl%20records%20on%20a%20wooden%20shelf%2C%20soft%20shadows%2C%20premium%20interior%20photography%2C%20no%20people%2C%20no%20text&width=1600&height=600&seq=av-cta-collect&orientation=landscape"
            alt="深色家庭影院角落，暖光与深红墙面"
            title="加入收藏夹 - av免费网站"
            className="w-full h-[320px] md:h-[380px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/30" />
          <div className="absolute inset-0 flex items-center">
            <div className="px-5 md:px-10 max-w-xl">
              <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">Keep Your List</p>
              <h2 className="mt-3 text-2xl md:text-4xl font-black leading-tight tracking-tight">
                把想看的先收进收藏夹
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 max-w-md">
                收藏记录保存在当前浏览器，下次打开不用重新翻找。看到感兴趣的先点一下喜欢，再慢慢挑时间看。
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/category/latest"
                  className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  开始浏览
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center h-11 px-6 rounded-md border border-background-400/60 text-sm text-foreground-800 hover:border-primary-500/60 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解本站
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}